import { ApiResponse } from '../types';
import {
  InternalFulfilmentDeliveryResponse,
  FulfilmentOptionValidationResponse,
  ShipCarrierFulfilmentOptionValidationRequest,
} from './types';
import { ApiClient } from '../client/ApiClient';
import { generateRecaptchaHeaders } from '../utils/recaptcha/generateRecaptchaHeaders';
import { CountryCode, getMultiTenancyHeaders } from '../utils/multiTenancyHeaders';
import { waitForAndGenerateRecaptchaToken } from '../utils/recaptcha/waitForAndGenerateRecaptchaToken';
import { getShopifyAuthHeader, ShopifyTokenGenerator } from '../utils/shopifySessionTokens';

const CARRIER_SERVICE_RECAPTCHA_ACTION = 'carrierServiceApi';

export class CarrierServiceClient extends ApiClient {
  private readonly _shipCarrierServiceBaseUrl: string;
  private readonly _shipCarrierValidatFulfilmentOptionUrl: string;
  private readonly _countryCode?: CountryCode;
  private readonly _shopifyTokenGenerator?: ShopifyTokenGenerator;

  constructor({
    shipCarrierServiceBaseUrl,
    shipCarrierValidatFulfilmentOptionUrl = '/ValidateDeliveryOption',
    countryCode,
    shopifyTokenGenerator,
  }: {
    shipCarrierServiceBaseUrl: string;
    shipCarrierValidatFulfilmentOptionUrl?: string;
    countryCode?: CountryCode;
    shopifyTokenGenerator?: ShopifyTokenGenerator;
  }) {
    super();
    this._shipCarrierServiceBaseUrl = shipCarrierServiceBaseUrl;
    this._shipCarrierValidatFulfilmentOptionUrl = shipCarrierValidatFulfilmentOptionUrl;
    this._countryCode = countryCode;
    this._shopifyTokenGenerator = shopifyTokenGenerator;
  }

  generateHeaders = async () => {
    const isInCheckoutExtensibility = this._shopifyTokenGenerator !== undefined;
    let userValidationHeaders: Record<string, string> = {};
    if (isInCheckoutExtensibility) {
      userValidationHeaders = (await getShopifyAuthHeader(this._shopifyTokenGenerator))!;
    } else {
      const { token, message } = await waitForAndGenerateRecaptchaToken(CARRIER_SERVICE_RECAPTCHA_ACTION);
      if (!token) {
        return { headers: null, error: message };
      }
      userValidationHeaders = generateRecaptchaHeaders(token, CARRIER_SERVICE_RECAPTCHA_ACTION);
    }
    return {
      headers: {
        ...userValidationHeaders,
        ...(this._countryCode ? getMultiTenancyHeaders(this._countryCode) : {}),
      },
      error: null,
    };
  };

  validateFulfilmentOption = async (
    body: ShipCarrierFulfilmentOptionValidationRequest
  ): Promise<ApiResponse<FulfilmentOptionValidationResponse>> => {
    const { headers: requestHeaders, error: headersError } = await this.generateHeaders();
    if (headersError || !requestHeaders) {
      return { statusCode: 500, error: new Error(headersError) };
    }

    const { data, statusCode, error } = await this._fetch<InternalFulfilmentDeliveryResponse>({
      path: `${this._shipCarrierServiceBaseUrl}${this._shipCarrierValidatFulfilmentOptionUrl}`,
      method: 'POST',
      body,
      headers: requestHeaders,
    });

    if (error) {
      return { statusCode, error };
    }

    if (!data) {
      return { statusCode, error: new Error('Carrier service did not return valid fulfilment option validation data') };
    }

    const processedData: FulfilmentOptionValidationResponse = data as any;

    return { data: processedData, statusCode };
  };
}
